import { Templates } from '@hellohair/types';
import { SvgIconComponent } from '@mui/icons-material';
import CampaignTwoToneIcon from '@mui/icons-material/CampaignTwoTone';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import AssignmentTurnedInTwoToneIcon from '@mui/icons-material/AssignmentTurnedInTwoTone';
import CheckBoxTwoToneIcon from '@mui/icons-material/CheckBoxTwoTone';
import FactCheckTwoToneIcon from '@mui/icons-material/FactCheckTwoTone';
import FlagTwoToneIcon from '@mui/icons-material/FlagTwoTone';
import GavelTwoToneIcon from '@mui/icons-material/GavelTwoTone';
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MailTwoToneIcon from '@mui/icons-material/MailTwoTone';
import MarkEmailReadTwoToneIcon from '@mui/icons-material/MarkEmailReadTwoTone';
import RadioButtonCheckedTwoToneIcon from '@mui/icons-material/RadioButtonCheckedTwoTone';
import StyleTwoToneIcon from '@mui/icons-material/StyleTwoTone';
import TextFieldsTwoToneIcon from '@mui/icons-material/TextFieldsTwoTone';
import WavingHandTwoToneIcon from '@mui/icons-material/WavingHandTwoTone';
import WebTwoToneIcon from '@mui/icons-material/WebTwoTone';

/**
 * Coarse grouping of the step types. The group decides the accent colour of a
 * step card, the type decides its icon - together they make a long step list
 * scannable without reading any label.
 */
export type StepGroup = 'start' | 'question' | 'upload' | 'legal' | 'end';

export const STEP_GROUPS: StepGroup[] = ['start', 'question', 'upload', 'legal', 'end'];

export const STEP_GROUP_COLOR: Record<StepGroup, string> = {
  start: '#24577D',
  question: '#7B4FA8',
  upload: '#0F8C8C',
  legal: '#C97C0B',
  end: '#2F8B57',
};

export const STEP_GROUP_LABEL_KEY: Record<StepGroup, string> = {
  start: 'editor.group.start',
  question: 'editor.group.question',
  upload: 'editor.group.upload',
  legal: 'editor.group.legal',
  end: 'editor.group.end',
};

export interface StepTypeMeta {
  /** The `type` discriminator persisted on the step. */
  type: string;
  group: StepGroup;
  icon: SvgIconComponent;
  labelKey: string;
  /** Row summary text. Defaults to the step's `question`. */
  preview?: (step: Templates.StepDataBase) => string | undefined;
}

export const STEP_TYPES: StepTypeMeta[] = [
  { type: 'Landing', group: 'start', icon: WebTwoToneIcon, labelKey: 'editor.type.Landing' },
  { type: 'Intro', group: 'start', icon: InfoTwoToneIcon, labelKey: 'editor.type.Intro' },
  { type: 'Interstitial', group: 'question', icon: CampaignTwoToneIcon, labelKey: 'editor.type.Interstitial' },
  { type: 'Welcome', group: 'start', icon: WavingHandTwoToneIcon, labelKey: 'editor.type.Welcome' },
  {
    type: 'SingleSelectAnswer',
    group: 'question',
    icon: RadioButtonCheckedTwoToneIcon,
    labelKey: 'editor.type.SingleSelectAnswer',
  },
  {
    type: 'MultiSelectAnswer',
    group: 'question',
    icon: CheckBoxTwoToneIcon,
    labelKey: 'editor.type.MultiSelectAnswer',
  },
  { type: 'PillSelect', group: 'question', icon: StyleTwoToneIcon, labelKey: 'editor.type.PillSelect' },
  { type: 'TextAnswer', group: 'question', icon: TextFieldsTwoToneIcon, labelKey: 'editor.type.TextAnswer' },
  { type: 'MailDoB', group: 'question', icon: MailTwoToneIcon, labelKey: 'editor.type.MailDoB' },
  { type: 'UploadImages', group: 'upload', icon: ImageTwoToneIcon, labelKey: 'editor.type.UploadImages' },
  { type: 'Legal', group: 'legal', icon: GavelTwoToneIcon, labelKey: 'editor.type.Legal' },
  { type: 'Accordion', group: 'legal', icon: ArticleTwoToneIcon, labelKey: 'editor.type.Accordion' },
  {
    type: 'Confirmation',
    group: 'end',
    icon: FactCheckTwoToneIcon,
    labelKey: 'editor.type.Confirmation',
    preview: (step) => step.title || step.question,
  },
  { type: 'Finish', group: 'end', icon: FlagTwoToneIcon, labelKey: 'editor.type.Finish' },
  { type: 'SelectFinish', group: 'end', icon: AssignmentTurnedInTwoToneIcon, labelKey: 'editor.type.SelectFinish' },
  { type: 'MailDoBFinish', group: 'end', icon: MarkEmailReadTwoToneIcon, labelKey: 'editor.type.MailDoBFinish' },
];

const BY_TYPE = new Map(STEP_TYPES.map((meta) => [meta.type, meta]));

const FALLBACK: StepTypeMeta = {
  type: 'Unknown',
  group: 'question',
  icon: TextFieldsTwoToneIcon,
  labelKey: 'editor.type.Unknown',
};

/** Never throws - an unknown persisted type still renders with a neutral look. */
export function stepTypeMeta(type: string | undefined): StepTypeMeta {
  return (type && BY_TYPE.get(type)) || FALLBACK;
}

export function stepTypesByGroup(group: StepGroup): StepTypeMeta[] {
  return STEP_TYPES.filter((meta) => meta.group === group);
}
